import { useCallback, useMemo } from 'react';
import { createColumnFilterRenderer } from '../../components/tables/createColumnFilterRenderer';
import {
  NotificationEvent,
  useGetAlarmsQuery,
  useGetMachinesQuery,
} from '../../gql/generated';
import { useNotificationFilters } from '../../hooks/useNotificationFilters';
import { toOptions } from '../../utils/options';

/**
 * Hook that returns a columnFilters render function for the notifications DataTable.
 * Each column gets its corresponding filter component.
 * Reads/writes filter state directly from URL query params - no props needed.
 */
export function useNotificationColumnFilters() {
  const { filters, setMachines, setAlarms } = useNotificationFilters();

  // Fetch machines for filter
  const { data: machinesData, loading: machinesLoading } = useGetMachinesQuery({
    variables: {
      pagination: {
        limit: 1000,
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  // Fetch alarms for filter
  const { data: alarmsData, loading: alarmsLoading } = useGetAlarmsQuery({
    variables: {
      pagination: {
        limit: 1000,
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  // Transform machine options - use id (GUID) as value (EDMS expects GUIDs)
  const machineOptions = useMemo(
    () =>
      toOptions(
        machinesData?.machines.rows,
        (machine) => machine.id,
        (machine) => machine.displayName || machine.machineId,
        true
      ),
    [machinesData]
  );

  // Transform alarm options - use id as value (matches backend filter)
  const alarmOptions = useMemo(
    () =>
      toOptions(
        alarmsData?.alarms?.rows,
        (alarm) => alarm.id,
        (alarm) => alarm.name,
        true
      ),
    [alarmsData]
  );

  const handleMachinesChange = useCallback(
    (values: string[]) => {
      setMachines(values);
    },
    [setMachines]
  );

  const handleAlarmsChange = useCallback(
    (values: string[]) => {
      setAlarms(values);
    },
    [setAlarms]
  );

  return createColumnFilterRenderer<NotificationEvent>([
    {
      columnId: 'machineName',
      kind: 'select',
      selectedValues: filters.machines,
      onChange: handleMachinesChange,
      options: machineOptions,
      placeholder: 'Search machines...',
      loading: machinesLoading,
    },
    {
      columnId: 'alarmName',
      kind: 'select',
      selectedValues: filters.alarms,
      onChange: handleAlarmsChange,
      options: alarmOptions,
      placeholder: 'Search Pulse alerts...',
      loading: alarmsLoading,
    },
  ]);
}
